import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Container,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import '../Styles/product.css';
import { useNavigate } from 'react-router-dom';
import { FaClipboardList, FaEdit, FaDownload, FaFolderOpen } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsQR from 'jsqr';
import {
  getAllCourses,
  getContentByCode,
  generateQRByNumber,
  getAllCourseDetails,
  getAllSubjectDetails,
  getContentForUrlMigration,
  migrateContentUrl,
  downloadContentUrlExcel,
} from '../Servies/AdmiServices';
import TableData from '../components/common/TableData';

const QR_BASE_URL = 'https://img-qrcode.alrn.in/qr-images/';
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.tif'];

export default function Courses() {
  const headers = ['Board Name', 'Course', 'Display Name', 'Status', 'Action'];
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);

  // --- Get Content by Code dialog ---
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [hashedStr, setHashedStr] = useState('');
  const [contentResult, setContentResult] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [qrDecoding, setQrDecoding] = useState(false);
  const qrFileInputRef = useRef(null);

  // --- Generate QR by Number dialog ---
  const [openGenQrDialog, setOpenGenQrDialog] = useState(false);
  const [genQrNumber, setGenQrNumber] = useState('');
  const [genQrLoading, setGenQrLoading] = useState(false);
  const [genQrError, setGenQrError] = useState('');

  // --- QR Images dialog ---
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDirs, setQrDirs] = useState([]);
  const [qrDirsLoading, setQrDirsLoading] = useState(false);
  const [qrDirsError, setQrDirsError] = useState('');
  const [downloadingDir, setDownloadingDir] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // --- Linking Content (URL migration) ---
  const [linkCourses, setLinkCourses] = useState([]);
  const [linkSubjects, setLinkSubjects] = useState([]);
  const [linkCourseId, setLinkCourseId] = useState('');
  const [linkSubjectId, setLinkSubjectId] = useState('');
  const [linkSubjectsLoading, setLinkSubjectsLoading] = useState(false);
  const [linkContentList, setLinkContentList] = useState([]);
  const [linkContentLoading, setLinkContentLoading] = useState(false);
  const [linkContentError, setLinkContentError] = useState('');
  const [targetCodes, setTargetCodes] = useState({});
  const [migrationResults, setMigrationResults] = useState({});
  const [migrating, setMigrating] = useState(false);
  const [migrationError, setMigrationError] = useState('');
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [exportContentCodes, setExportContentCodes] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportExcelError, setExportExcelError] = useState('');

  const handleOpenLinkDialog = () => setOpenLinkDialog(true);
  const handleCloseLinkDialog = () => {
    if (migrating || exportingExcel) return;
    setOpenLinkDialog(false);
  };

  useEffect(() => {
    getAllCourseDetails()
      .then((res) => setLinkCourses(res?.data || []))
      .catch(() => {});
  }, []);

  const handleLinkCourseChange = (courseId) => {
    setLinkCourseId(courseId);
    setLinkSubjectId('');
    setLinkSubjects([]);
    setLinkContentList([]);
    setTargetCodes({});
    setMigrationResults({});
    setLinkContentError('');
    if (!courseId) return;
    setLinkSubjectsLoading(true);
    getAllSubjectDetails(courseId)
      .then((res) => setLinkSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setLinkSubjectsLoading(false));
  };

  const handleLoadLinkContent = async () => {
    if (!linkCourseId || !linkSubjectId) {
      setLinkContentError('Please select both a course and a subject.');
      return;
    }
    setLinkContentLoading(true);
    setLinkContentError('');
    setLinkContentList([]);
    setTargetCodes({});
    setMigrationResults({});
    try {
      const res = await getContentForUrlMigration(linkCourseId, linkSubjectId);
      setLinkContentList(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setLinkContentError('Failed to load content for the selected course/subject.');
    } finally {
      setLinkContentLoading(false);
    }
  };

  const handleTargetCodeChange = (contentId, value) => {
    setTargetCodes((prev) => ({ ...prev, [contentId]: value }));
  };

  const handleMigrateContentUrls = async () => {
    const itemsToSend = linkContentList.filter((item) => (targetCodes[item.contentId] || '').trim());
    if (itemsToSend.length === 0) {
      setMigrationError('Enter at least one target content code before migrating.');
      return;
    }

    setMigrating(true);
    setMigrationError('');
    try {
      const pairs = itemsToSend.map((item) => ({
        sourceContentCode: item.contentCode,
        targetContentCode: targetCodes[item.contentId].trim(),
      }));
      const res = await migrateContentUrl(pairs);
      const results = Array.isArray(res?.data) ? res.data : [];
      const resultsByContentId = {};
      itemsToSend.forEach((item, index) => {
        resultsByContentId[item.contentId] = results[index];
      });
      setMigrationResults(resultsByContentId);
    } catch (err) {
      setMigrationError('Failed to migrate content URLs.');
    } finally {
      setMigrating(false);
    }
  };

  const handleDownloadContentUrlExcel = async () => {
    const contentCodes = exportContentCodes
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean)
      .join(',');

    if (!contentCodes) {
      setExportExcelError('Please enter at least one content code.');
      return;
    }

    setExportingExcel(true);
    setExportExcelError('');
    try {
      const res = await downloadContentUrlExcel(contentCodes);
      if (res.status !== 200) {
        const errorText = await res.data.text();
        setExportExcelError(errorText || 'Failed to download the Excel file.');
        return;
      }
      const disposition = res.headers?.['content-disposition'];
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `content_url_export_${Date.now()}.xlsx`;
      saveAs(res.data, fileName);
    } catch (err) {
      setExportExcelError('Failed to download the Excel file.');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleEditEdit = (courseId) => {
    navigate(`/dashboard/CreateCourse?courseId=${courseId}`);
  };

  const handleStructure = (courseId) => {
    navigate(`/dashboard/CourseStructure?courseId=${courseId}`);
  };

  const getCoursesList = () => {
    getAllCourses()
      .then((res) => setCourseData(res?.data))
      .catch(() => {});
  };

  useEffect(() => {
    getCoursesList();
  }, []);

  // ---- Get Content by Code ----
  const handleOpenContentDialog = () => {
    setOpenContentDialog(true);
    setHashedStr('');
    setContentResult(null);
    setContentError('');
  };

  const handleCloseContentDialog = () => setOpenContentDialog(false);

  const handleFetchContent = async (overrideStr) => {
    const strToSend = (overrideStr ?? hashedStr).trim();
    if (!strToSend) {
      setContentError('Please enter a hashed string.');
      return;
    }
    setContentLoading(true);
    setContentError('');
    setContentResult(null);
    try {
      const res = await getContentByCode(strToSend);
      if (res?.data?.success) {
        setContentResult(res.data);
      } else {
        setContentError(res?.data?.message || 'Failed to fetch content.');
      }
    } catch {
      setContentError('An error occurred while fetching content.');
    } finally {
      setContentLoading(false);
    }
  };

  // ---- Upload QR image -> decode -> extract Data field -> fetch content ----
  const handleUploadQrClick = () => {
    qrFileInputRef.current?.click();
  };

  const handleQrFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setContentError('');
    setContentResult(null);
    setQrDecoding(true);

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

        setQrDecoding(false);

        if (!qrCode) {
          setContentError('Could not read a QR code from the uploaded image.');
          return;
        }

        let parsed;
        try {
          parsed = JSON.parse(qrCode.data);
        } catch {
          setContentError('QR code did not contain valid data.');
          return;
        }

        const rawData = parsed?.Data;
        if (!rawData) {
          setContentError('QR code data is missing the expected "Data" field.');
          return;
        }

        const cleanedData = rawData.replace(/=+$/, '');
        setHashedStr(cleanedData);
        handleFetchContent(cleanedData);
      };
      img.onerror = () => {
        setQrDecoding(false);
        setContentError('Could not load the uploaded image.');
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      setQrDecoding(false);
      setContentError('Could not read the uploaded file.');
    };
    reader.readAsDataURL(file);
  };

  // ---- Generate QR by Number ----
  const handleOpenGenQrDialog = () => {
    setOpenGenQrDialog(true);
    setGenQrNumber('');
    setGenQrError('');
  };

  const handleCloseGenQrDialog = () => {
    if (genQrLoading) return;
    setOpenGenQrDialog(false);
  };

  const handleGenerateQR = async () => {
    const num = parseInt(genQrNumber, 10);
    if (!genQrNumber || Number.isNaN(num) || num < 1) {
      setGenQrError('Please enter a valid positive number.');
      return;
    }
    setGenQrLoading(true);
    setGenQrError('');
    try {
      // Step 1: trigger server-side generation
      const response = await generateQRByNumber(num);
      if (!response.ok) throw new Error(`Server error (HTTP ${response.status})`);

      // Step 2: find the latest directory created on the image server
      const dirRes = await fetch(QR_BASE_URL);
      if (!dirRes.ok) throw new Error('Could not fetch directory listing after generation');
      const dirs = await dirRes.json();
      const latest = dirs
        .filter((d) => d.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))[0];

      if (!latest) throw new Error('No directory found after generation');

      // Step 3: download its images as zip using same logic as folder list
      await buildAndDownloadZip(latest.name, () => {});
      setOpenGenQrDialog(false);
    } catch (err) {
      setGenQrError(`Failed: ${err.message}`);
    } finally {
      setGenQrLoading(false);
    }
  };

  // ---- QR Images ----
  const handleOpenQrDialog = async () => {
    setOpenQrDialog(true);
    setQrDirs([]);
    setQrDirsError('');
    setQrDirsLoading(true);
    try {
      const res = await fetch(QR_BASE_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const sorted = data
        .filter((item) => item.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))
        .slice(0, 15);
      setQrDirs(sorted);
    } catch (err) {
      setQrDirsError(`Failed to load directories: ${err.message}`);
    } finally {
      setQrDirsLoading(false);
    }
  };

  const handleCloseQrDialog = () => {
    if (downloadingDir) return;
    setOpenQrDialog(false);
  };

  const buildAndDownloadZip = async (dirName, onProgress) => {
    const listRes = await fetch(`${QR_BASE_URL}${dirName}/`);
    if (!listRes.ok) throw new Error(`Could not list directory (HTTP ${listRes.status})`);
    const files = await listRes.json();

    const imageFiles = files.filter(
      (f) => f.type === 'file' && IMAGE_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
    );

    if (imageFiles.length === 0) throw new Error(`No images found in "${dirName}".`);

    onProgress({ current: 0, total: imageFiles.length });

    const zip = new JSZip();
    const folder = zip.folder(dirName);

    await Promise.all(
      imageFiles.map(async (file) => {
        const imgRes = await fetch(`${QR_BASE_URL}${dirName}/${file.name}`);
        const blob = await imgRes.blob();
        folder.file(file.name, blob);
        onProgress((prev) => ({ ...prev, current: prev.current + 1 }));
      })
    );

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${dirName}.zip`);
  };

  const handleDownloadDirZip = async (dirName) => {
    setDownloadingDir(dirName);
    setDownloadProgress({ current: 0, total: 0 });
    try {
      await buildAndDownloadZip(dirName, setDownloadProgress);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingDir(null);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ---- Table columns ----
  const specificColumns = courseData.map((item) => ({
    'Board Name': item.boardName,
    Course: item.course,
    'Display Name': item.displayName,
    Status: item.status ? (
      <Button variant="contained" color="primary">Active</Button>
    ) : (
      <Button variant="contained" color="error">Inactive</Button>
    ),
    Action: (
      <div>
        <FaEdit
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleEditEdit(item.courseId)}
        />
        <FaClipboardList
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleStructure(item.courseId)}
        />
      </div>
    ),
  }));

  const content = contentResult?.content;
  const hashData = (() => {
    try {
      return contentResult?.hash_data ? JSON.parse(contentResult.hash_data) : null;
    } catch {
      return null;
    }
  })();

  const isDownloading = !!downloadingDir;

  return (
    <>
      <Helmet>
        <title> Dashboard: Courses </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Courses
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<FaFolderOpen />} onClick={handleOpenQrDialog}>
              QR Images
            </Button>
            <Button variant="outlined" onClick={handleOpenGenQrDialog}>
              Generate QR
            </Button>
            <Button variant="outlined" onClick={handleOpenContentDialog}>
              Get Content by Code
            </Button>
            <Button variant="outlined" onClick={handleOpenLinkDialog}>
              Linking Course
            </Button>
            <Button variant="contained" onClick={() => navigate('/dashboard/CreateCourse')}>
              Create Course
            </Button>
          </Stack>
        </Stack>

        <TableData data={specificColumns} headers={headers} rowsPerPageOptions={[5, 10, 20]} />
      </Container>

      {/* ---- Linking Course (content URL migration) Dialog ---- */}
      <Dialog open={openLinkDialog} onClose={handleCloseLinkDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Linking Course</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Copy a content item's file URL from one course/subject to a target content code in another course.
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" mt={2} flexWrap="wrap" rowGap={2}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="link-course-label">Source Course</InputLabel>
              <Select
                labelId="link-course-label"
                label="Source Course"
                value={linkCourseId}
                onChange={(e) => handleLinkCourseChange(e.target.value)}
              >
                {linkCourses.map((course) => (
                  <MenuItem key={course.courseId} value={course.courseId}>
                    {course.courseDisplayName || course.courseId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 240 }} disabled={!linkCourseId || linkSubjectsLoading}>
              <InputLabel id="link-subject-label">Source Subject</InputLabel>
              <Select
                labelId="link-subject-label"
                label="Source Subject"
                value={linkSubjectId}
                onChange={(e) => setLinkSubjectId(e.target.value)}
              >
                {linkSubjects.map((subject) => (
                  <MenuItem key={subject.levelId} value={subject.levelId}>
                    {subject.levelDisplayName || subject.levelId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleLoadLinkContent}
              disabled={!linkCourseId || !linkSubjectId || linkContentLoading}
            >
              {linkContentLoading ? <CircularProgress size={20} color="inherit" /> : 'Load Content'}
            </Button>
          </Stack>

          {linkContentError && (
            <Typography color="error" variant="body2" mt={2}>
              {linkContentError}
            </Typography>
          )}

          {linkContentList.length > 0 && (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Content Code</strong></TableCell>
                      <TableCell><strong>Display Name</strong></TableCell>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell><strong>File URL</strong></TableCell>
                      <TableCell><strong>Target Content Code</strong></TableCell>
                      <TableCell><strong>Result</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linkContentList.map((item) => {
                      const result = migrationResults[item.contentId];
                      return (
                        <TableRow key={item.contentId} hover>
                          <TableCell>{item.contentCode}</TableCell>
                          <TableCell>{item.contentDisplayName}</TableCell>
                          <TableCell>{item.levelDisplayName}</TableCell>
                          <TableCell>
                            {item.fileUrl ? (
                              <Typography
                                variant="caption"
                                sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                              >
                                {item.fileUrl}
                              </Typography>
                            ) : (
                              <Chip label="No URL" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="e.g. CRS002SUB01CH01L01"
                              value={targetCodes[item.contentId] || ''}
                              onChange={(e) => handleTargetCodeChange(item.contentId, e.target.value)}
                              disabled={!item.fileUrl}
                            />
                          </TableCell>
                          <TableCell>
                            {result && (
                              <Chip
                                label={result.message}
                                size="small"
                                color={result.success ? 'success' : 'error'}
                                sx={{ maxWidth: 260 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {migrationError && (
                <Typography color="error" variant="body2" mt={2}>
                  {migrationError}
                </Typography>
              )}

              <Stack direction="row" justifyContent="flex-end" mt={2}>
                <Button variant="contained" onClick={handleMigrateContentUrls} disabled={migrating}>
                  {migrating ? <CircularProgress size={20} color="inherit" /> : 'Migrate Selected'}
                </Button>
              </Stack>
            </>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Download Content URLs as Excel
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Paste content codes (comma or newline separated) to export their file URLs to an Excel file.
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder={'e.g. CRS001SUB01CH01L01, CRS001SUB01CH01L02, CRS001SUB01CH02L01'}
            value={exportContentCodes}
            onChange={(e) => setExportContentCodes(e.target.value)}
            sx={{ mt: 2 }}
          />

          {exportExcelError && (
            <Typography color="error" variant="body2" mt={2}>
              {exportExcelError}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              startIcon={!exportingExcel ? <FaDownload /> : null}
              onClick={handleDownloadContentUrlExcel}
              disabled={exportingExcel}
            >
              {exportingExcel ? <CircularProgress size={20} color="inherit" /> : 'Download Excel'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={migrating || exportingExcel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Generate QR by Number Dialog ---- */}
      <Dialog open={openGenQrDialog} onClose={handleCloseGenQrDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Generate QR Codes by Number</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Number"
              type="number"
              value={genQrNumber}
              onChange={(e) => setGenQrNumber(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
              placeholder="e.g. 3"
              error={!!genQrError}
              helperText={genQrError}
              disabled={genQrLoading}
            />
            {genQrLoading && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Generating QR codes...
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenQrDialog} disabled={genQrLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateQR} disabled={genQrLoading}>
            Generate & Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- QR Images Dialog ---- */}
      <Dialog open={openQrDialog} onClose={handleCloseQrDialog} maxWidth="md" fullWidth>
        <DialogTitle>QR Image Directories</DialogTitle>
        <DialogContent>
          {qrDirsLoading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={30} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Loading directories...
              </Typography>
            </Stack>
          )}

          {qrDirsError && (
            <Typography color="error" variant="body2" mt={1}>
              {qrDirsError}
            </Typography>
          )}

          {!qrDirsLoading && !qrDirsError && qrDirs.length === 0 && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              No directories found.
            </Typography>
          )}

          {qrDirs.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Directory Name</strong></TableCell>
                    <TableCell><strong>Modified Date</strong></TableCell>
                    <TableCell align="center"><strong>Download</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrDirs.map((dir) => {
                    const isThisDownloading = downloadingDir === dir.name;
                    return (
                      <TableRow key={dir.name} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FaFolderOpen color="#f5a623" />
                            <Typography variant="body2">{dir.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(dir.mtime).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {isThisDownloading ? (
                            <Stack alignItems="center" spacing={0.5}>
                              <CircularProgress size={18} />
                              {downloadProgress.total > 0 && (
                                <>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(downloadProgress.current / downloadProgress.total) * 100}
                                    sx={{ width: 80 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {downloadProgress.current}/{downloadProgress.total}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                          ) : (
                            <Tooltip title={`Download ${dir.name}.zip`}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadDirZip(dir.name)}
                                  disabled={isDownloading}
                                >
                                  <FaDownload />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQrDialog} disabled={isDownloading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Get Content by Code Dialog ---- */}
      <Dialog open={openContentDialog} onClose={handleCloseContentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Get Content by Hashed Code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Hashed String"
              value={hashedStr}
              onChange={(e) => setHashedStr(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Paste the hashed string here, or upload a QR code image..."
              error={!!contentError && !contentLoading && !qrDecoding}
              helperText={!contentLoading && !qrDecoding && contentError}
            />

            <input
              type="file"
              accept="image/*"
              ref={qrFileInputRef}
              style={{ display: 'none' }}
              onChange={handleQrFileChange}
            />
            <Button
              variant="outlined"
              onClick={handleUploadQrClick}
              disabled={qrDecoding || contentLoading}
              startIcon={qrDecoding ? <CircularProgress size={16} /> : null}
            >
              {qrDecoding ? 'Reading QR Code...' : 'Upload QR Code'}
            </Button>

            {contentLoading && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {contentResult && content && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                {hashData && (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                      {Object.entries(hashData).map(([k, v]) => (
                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" />
                      ))}
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />
                  </>
                )}

                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {content.contentName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {content.contentDescription}
                </Typography>

                <Stack spacing={0.5} mt={1}>
                  <InfoRow label="Content Code" value={content.contentCode} />
                  <InfoRow label="Display Name" value={content.contentDisplayName} />
                  <InfoRow
                    label="Type"
                    value={content.contentTypeCode === 1 ? 'AV (Audio/Video)' : content.contentTypeCode}
                  />
                  <InfoRow label="Sequence No" value={content.sequenceNo} />
                  <InfoRow label="Duration" value={`${content.duration}s`} />
                  <InfoRow
                    label="Status"
                    value={
                      <Chip
                        label={content.isActive ? 'Active' : 'Inactive'}
                        color={content.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </Stack>

                {content.fileURL && (
                  <Box mt={2}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Media URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {content.fileURL}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog}>Close</Button>
          <Button variant="contained" onClick={() => handleFetchContent()} disabled={contentLoading}>
            Fetch Content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}:
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  );
}
